/**
 * Centralized layer/source ID generator for Nowcasting map instances.
 *
 * Each Mapbox map (main, split, PiP) needs unique IDs for sources, layers,
 * and sprite images. This helper returns the exact strings already in use
 * so that existing behavior is preserved while enabling code reuse.
 */

export type MapRole = 'main' | 'split' | 'pip';

export interface NowcastingMapIds {
  role: MapRole;

  // Circuit track
  circuitSource: string;
  circuitLineLayer: string;

  // Weather stations
  stationsSource: string;
  stationsLayer: string;
  stationsLabelsLayer: string;

  // Wind arrows
  windArrowsSource: string;
  windArrowsLayer: string;
  windArrowImagePrefix: string;

  // Radar A/B double-buffer
  radarSourceA: string;
  radarLayerA: string;
  radarSourceB: string;
  radarLayerB: string;

  // Numeric radar A/B double-buffer (main only; split/PiP reuse main blob)
  radarNumericSourceA: string;
  radarNumericLayerA: string;
  radarNumericSourceB: string;
  radarNumericLayerB: string;

  // Tomorrow.io A/B double-buffer
  tioSourceA: string;
  tioLayerA: string;
  tioSourceB: string;
  tioLayerB: string;

  // Circle overlay (3 km ring)
  circleOverlaySource: string;
  circleOverlayLayer: string;

  // Radar bounding box overlay
  radarBboxSource: string;
  radarBboxLayer: string;

  // Radius circles (indexed: append `{i}` to each prefix)
  radiusCircleSourcePrefix: string;
  radiusCircleLayerPrefix: string;
  radiusLabelSourcePrefix: string;
  radiusLabelLayerPrefix: string;

  // Zoom indicator DOM attribute
  zoomIndicatorAttr: string;
}

function mainIds(): NowcastingMapIds {
  return {
    role: 'main',

    circuitSource: 'circuit',
    circuitLineLayer: 'circuit-line',
    stationsSource: 'circuit-stations',
    stationsLayer: 'circuit-stations',
    stationsLabelsLayer: 'circuit-stations-labels',

    windArrowsSource: 'wind-arrows',
    windArrowsLayer: 'wind-arrows',
    windArrowImagePrefix: '',

    radarSourceA: 'radar-tiles',
    radarLayerA: 'radar-layer',
    radarSourceB: 'radar-tiles-2',
    radarLayerB: 'radar-layer-2',

    radarNumericSourceA: 'radar-numeric-tiles',
    radarNumericLayerA: 'radar-numeric-layer',
    radarNumericSourceB: 'radar-numeric-tiles-2',
    radarNumericLayerB: 'radar-numeric-layer-2',

    tioSourceA: 'tio-tiles',
    tioLayerA: 'tio-layer',
    tioSourceB: 'tio-tiles-2',
    tioLayerB: 'tio-layer-2',

    circleOverlaySource: 'circuit-circle-overlay',
    circleOverlayLayer: 'circuit-circle-overlay-layer',

    radarBboxSource: 'radar-bbox-overlay',
    radarBboxLayer: 'radar-bbox-overlay-layer',

    radiusCircleSourcePrefix: 'radius-circle-',
    radiusCircleLayerPrefix: 'radius-circle-layer-',
    radiusLabelSourcePrefix: 'radius-label-',
    radiusLabelLayerPrefix: 'radius-label-layer-',

    zoomIndicatorAttr: 'data-zoom-indicator',
  };
}

function splitIds(): NowcastingMapIds {
  return {
    role: 'split',

    circuitSource: 'split-circuit',
    circuitLineLayer: 'split-circuit-line',
    stationsSource: 'split-circuit-stations',
    stationsLayer: 'split-circuit-stations',
    stationsLabelsLayer: 'split-circuit-stations-labels',

    windArrowsSource: 'split-wind-arrows',
    windArrowsLayer: 'split-wind-arrows',
    windArrowImagePrefix: 'split-',

    radarSourceA: 'split-radar-tiles',
    radarLayerA: 'split-radar-layer',
    radarSourceB: 'split-radar-tiles-2',
    radarLayerB: 'split-radar-layer-2',

    radarNumericSourceA: 'split-radar-numeric-tiles',
    radarNumericLayerA: 'split-radar-numeric-layer',
    radarNumericSourceB: 'split-radar-numeric-tiles-2',
    radarNumericLayerB: 'split-radar-numeric-layer-2',

    tioSourceA: 'split-tio-tiles',
    tioLayerA: 'split-tio-layer',
    tioSourceB: 'split-tio-tiles-2',
    tioLayerB: 'split-tio-layer-2',

    circleOverlaySource: 'split-circle-overlay',
    circleOverlayLayer: 'split-circle-overlay-layer',

    radarBboxSource: 'split-radar-bbox-overlay',
    radarBboxLayer: 'split-radar-bbox-overlay-layer',

    // Split currently uses the same prefix for source and layer (existing behavior)
    radiusCircleSourcePrefix: 'split-radius-circle-',
    radiusCircleLayerPrefix: 'split-radius-circle-',
    radiusLabelSourcePrefix: 'split-radius-label-',
    radiusLabelLayerPrefix: 'split-radius-label-layer-',

    zoomIndicatorAttr: 'data-split-zoom-indicator',
  };
}

function pipIds(): NowcastingMapIds {
  return {
    role: 'pip',

    circuitSource: 'pip-circuit',
    circuitLineLayer: 'pip-circuit-line',
    stationsSource: 'pip-circuit-stations',
    stationsLayer: 'pip-circuit-stations',
    stationsLabelsLayer: 'pip-circuit-stations-labels',

    windArrowsSource: 'pip-wind-arrows',
    windArrowsLayer: 'pip-wind-arrows',
    windArrowImagePrefix: 'pip-',

    radarSourceA: 'pip-radar-tiles',
    radarLayerA: 'pip-radar-layer',
    radarSourceB: 'pip-radar-tiles-2',
    radarLayerB: 'pip-radar-layer-2',

    radarNumericSourceA: 'pip-radar-numeric-tiles',
    radarNumericLayerA: 'pip-radar-numeric-layer',
    radarNumericSourceB: 'pip-radar-numeric-tiles-2',
    radarNumericLayerB: 'pip-radar-numeric-layer-2',

    tioSourceA: 'pip-tio-tiles',
    tioLayerA: 'pip-tio-layer',
    tioSourceB: 'pip-tio-tiles-2',
    tioLayerB: 'pip-tio-layer-2',

    circleOverlaySource: 'pip-circle-overlay',
    circleOverlayLayer: 'pip-circle-overlay-layer',

    radarBboxSource: 'pip-radar-bbox-overlay',
    radarBboxLayer: 'pip-radar-bbox-overlay-layer',

    radiusCircleSourcePrefix: 'pip-radius-circle-',
    radiusCircleLayerPrefix: 'pip-radius-circle-layer-',
    radiusLabelSourcePrefix: 'pip-radius-label-',
    radiusLabelLayerPrefix: 'pip-radius-label-layer-',

    zoomIndicatorAttr: 'data-pip-zoom-indicator',
  };
}

const IDS_BY_ROLE: Record<MapRole, NowcastingMapIds> = {
  main: mainIds(),
  split: splitIds(),
  pip: pipIds(),
};

export function getMapIds(role: MapRole): NowcastingMapIds {
  return IDS_BY_ROLE[role];
}

/**
 * Resolves the Mapbox style URL from the user's chosen style key,
 * falling back to theme-based defaults.
 */
export function resolveMapStyle(
  mapStyle: string | null,
  mapStyles: Record<string, { name: string; url: string }>,
  theme: string,
): string {
  if (mapStyle && mapStyles[mapStyle as keyof typeof mapStyles]) {
    return mapStyles[mapStyle as keyof typeof mapStyles].url;
  }
  return theme === 'dark'
    ? 'mapbox://styles/mapbox/dark-v11'
    : 'mapbox://styles/mapbox/light-v11';
}
